import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AllocationsTable,
  AllocationDrawer,
  DeleteAllocationDialog,
} from '@/components/allocations'
import { useAllocations } from '@/hooks/useAllocations'
import { useAppContext } from '@/hooks'

export function AllocationsPage() {
  // Get location from URL context
  const { locationId, locationName, scope } = useAppContext()
  const [selectedAllocationId, setSelectedAllocationId] = useState<string | undefined>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deleteAllocationId, setDeleteAllocationId] = useState<string | undefined>()
  const [deleteAllocationName, setDeleteAllocationName] = useState<string | undefined>()

  // Filter by location when in location scope
  const filters = scope === 'location' && locationId ? { locationId } : undefined
  const { data: allocations = [], isLoading } = useAllocations(filters)

  const selectedAllocation = selectedAllocationId
    ? allocations.find((a) => a.id === selectedAllocationId)
    : undefined

  const handleCreate = () => {
    setSelectedAllocationId(undefined)
    setIsDrawerOpen(true)
  }

  const handleEdit = (id: string) => {
    setSelectedAllocationId(id)
    setIsDrawerOpen(true)
  }

  const handleDelete = (id: string, tailNumber?: string) => {
    setDeleteAllocationId(id)
    setDeleteAllocationName(tailNumber)
    setIsDeleteDialogOpen(true)
  }

  const handleDrawerOpenChange = (open: boolean) => {
    setIsDrawerOpen(open)
    if (!open) {
      setSelectedAllocationId(undefined)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Aircraft Allocations</h1>
        <p className="text-muted-foreground">
          Manage aircraft positions and allocations at your facilities
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Current Allocations</CardTitle>
            <CardDescription>
              {scope === 'location' && locationName
                ? `Allocations at ${locationName}`
                : 'Allocations across all locations'}
            </CardDescription>
          </div>
          <Button onClick={handleCreate} size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            New Allocation
          </Button>
        </CardHeader>
        <CardContent>
          <AllocationsTable
            data={allocations}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <AllocationDrawer
        open={isDrawerOpen}
        onOpenChange={handleDrawerOpenChange}
        allocation={selectedAllocation}
      />

      <DeleteAllocationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        allocationId={deleteAllocationId || ''}
        allocationName={deleteAllocationName}
      />
    </div>
  )
}
